// Pieces the workbench, the planned-orders list, the drawer and the run detail
// share (spec 03 §4.1, §4.5): exact quantities, the chips, a run's error in
// words and the Run MRP control with its poll.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import {
  requestMrpGroupPass,
  requestMrpRun,
  useMrpRefresh,
  useMrpRun,
  type ActionMessageKind,
  type MrpGroupPassAccepted,
  type MrpRun,
  type MrpRunStatus,
  type MrpRunTrigger,
  type PlannedOrderConvertedKind,
  type PlannedOrderKind,
  type PlannedOrderStatus,
} from "../hooks/mrp-queries";
import { problemExtra } from "../hooks/planning-queries";
import { formatExactQty } from "../lib/mrp-lib";
import { useOffline } from "../lib/planning-lib";
import { shortId } from "../../../../lib/short-id";

/**
 * A quantity formatted in the reader's locale from its digits, with the raw
 * decimal kept as the element's value and title for copying (spec 03 §4).
 */
export function ExactQty({
  qty,
  unit,
  signed = false,
  testId,
}: {
  qty: string;
  unit?: string | undefined;
  signed?: boolean;
  testId?: string;
}) {
  const { i18n } = useTranslation();
  return (
    <span className="num planning-qty" data-testid={testId} data-qty={qty}>
      <data value={qty} title={qty}>
        {formatExactQty(qty, i18n.language, signed)}
      </data>
      {unit !== undefined ? <span className="planning-unit"> {unit}</span> : null}
    </span>
  );
}

/**
 * The document a converted order became (§4.5, D-389 rulings 4 and 5): a draft
 * work order links to module 04's detail and a draft transfer to module 06's,
 * each of which answers its own reader. A click inside a row does not open the
 * row's drawer.
 */
export function ConvertedDocument({
  kind,
  id,
  testId = "planning-order-document",
}: {
  kind: PlannedOrderConvertedKind;
  id: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const words = t(`planning.orders.document.${kind}`);
  const common = {
    "data-testid": testId,
    "data-kind": kind,
    "data-ref-id": id,
    onClick: (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
    },
  };
  if (kind === "work_order") {
    return (
      <Link to="/production/work-orders/$id" params={{ id }} {...common}>
        {words}
      </Link>
    );
  }
  if (kind === "transfer_order") {
    return (
      <Link to="/inventory/transfers/$id" params={{ id }} {...common}>
        {words}
      </Link>
    );
  }
  return <span {...common}>{words}</span>;
}

const KIND_VARIANT: Readonly<Record<PlannedOrderKind, ChipVariant>> = {
  manufacture: "accent",
  purchase: "neutral",
  transfer: "neutral",
};

export function PlannedOrderKindChip({ kind }: { kind: PlannedOrderKind }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={KIND_VARIANT[kind]}
      dot={false}
      data-status={kind}
      data-testid="planning-order-kind"
    >
      {t(`planning.orders.kind.${kind}`)}
    </StatusChip>
  );
}

const STATUS_VARIANT: Readonly<Record<PlannedOrderStatus, ChipVariant>> = {
  proposed: "accent",
  firmed: "ok",
  converted: "ok",
  cancelled: "neutral",
};

export function PlannedOrderStatusChip({ status }: { status: PlannedOrderStatus }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={STATUS_VARIANT[status]}
      data-status={status}
      data-testid="planning-order-status"
    >
      {t(`planning.orders.status.${status}`)}
    </StatusChip>
  );
}

/** §4.5's red message badge. */
export function ActionMessageChip({ kind }: { kind: ActionMessageKind }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant="bad" data-status={kind} data-testid="planning-order-message">
      {t(`planning.orders.message.${kind}`)}
    </StatusChip>
  );
}

const RUN_VARIANT: Readonly<Record<MrpRunStatus, ChipVariant>> = {
  queued: "neutral",
  running: "accent",
  succeeded: "ok",
  failed: "bad",
};

export function MrpRunStatusChip({
  status,
  amber = false,
  testId,
}: {
  status: MrpRunStatus;
  /** A succeeded run that left products out for a graph error (§4.1). */
  amber?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={amber && status === "succeeded" ? "warn" : RUN_VARIANT[status]}
      data-status={status}
      data-testid={testId ?? "planning-mrp-run-status"}
    >
      {t(`planning.mrp.status.${status}`)}
    </StatusChip>
  );
}

export function MrpTriggerChip({ trigger }: { trigger: MrpRunTrigger }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant="neutral"
      dot={false}
      data-status={trigger}
      data-testid="planning-mrp-run-trigger"
    >
      {t(`planning.mrp.trigger.${trigger}`)}
    </StatusChip>
  );
}

/** A failed run's error key in words, with the code its detail names (keys and ids only, §2.9). */
export function mrpErrorText(t: TFunction, run: Pick<MrpRun, "error" | "error_detail">): string {
  if (run.error === null) return "";
  const sentence = t(`planning.mrp.error.${run.error}`);
  const detail = run.error_detail ?? {};
  const code =
    typeof detail["reason"] === "string"
      ? detail["reason"]
      : typeof detail["refresh_error"] === "string"
        ? detail["refresh_error"]
        : undefined;
  return code === undefined ? sentence : `${sentence} (${code})`;
}

/** The error, with the calendars screen linked when the facility has no calendar (D-386 ruling 4). */
export function MrpErrorLine({ run }: { run: Pick<MrpRun, "error" | "error_detail"> }) {
  const { t } = useTranslation();
  if (run.error === null) return null;
  return (
    <span className="planning-bad" data-testid="planning-mrp-run-error" data-error={run.error}>
      {mrpErrorText(t, run)}
      {run.error === "FACILITY_CALENDAR_MISSING" ? (
        <>
          {" "}
          <Link
            to="/planning/settings/calendars"
            data-testid="planning-mrp-calendar-link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {t("planning.mrp.openCalendars")}
          </Link>
        </>
      ) : null}
    </span>
  );
}

/**
 * The running pass's id a 409 `MRP_GROUP_PASS_RUNNING` names, shortened, or
 * null when the 409 names none: it does only while the pass has a run queued
 * or running (spec 03 §3.3, D-609).
 */
function orchestrationShort(failure: unknown): string | null {
  const id = problemExtra(failure, "orchestration_id");
  return typeof id === "string" ? shortId(id) : null;
}

function groupRunningText(t: TFunction, failure: unknown): string {
  const id = orchestrationShort(failure);
  return id === null
    ? t("planning.mrp.groupRunningUnnamed")
    : t("planning.mrp.groupRunning", { id });
}

export interface MrpRunControl {
  readonly running: boolean;
  readonly start: () => void;
  readonly button: ReactNode;
  readonly groupButton: ReactNode;
  readonly banner: ReactNode;
}

/**
 * Run MRP (`planning.mrp.run`): queues the facility's run, or joins the one
 * already queued, polls it until it ends, then re-reads every MRP read of the
 * facility. Run group-wide starts the manual pass and states its order and its
 * skips; the server decides the group scope (§5 row 39).
 */
export function useMrpRunControl(
  facilityId: string,
  facilityName: (id: string) => string,
  timezone: string | null | undefined,
  options: { canRun: boolean; canGroup: boolean },
): MrpRunControl {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const refresh = useMrpRefresh();
  const [watching, setWatching] = useState<string | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const [outcome, setOutcome] = useState<MrpRun | undefined>(undefined);
  const [group, setGroup] = useState<MrpGroupPassAccepted | undefined>(undefined);
  const [grouping, setGrouping] = useState(false);
  const run = useMrpRun(watching);

  useEffect(() => {
    setWatching(undefined);
    setOutcome(undefined);
    setFailure(undefined);
    setGroup(undefined);
  }, [facilityId]);

  const done =
    run.data !== undefined && (run.data.status === "succeeded" || run.data.status === "failed")
      ? run.data
      : undefined;
  useEffect(() => {
    if (done === undefined) return;
    setOutcome(done);
    setWatching(undefined);
    void refresh(facilityId);
  }, [done, facilityId, refresh]);

  const fail = (error: unknown) => {
    if (error instanceof UnauthorizedError) {
      void navigate({ to: "/login" });
      return;
    }
    setFailure(error);
  };

  const start = async () => {
    setStarting(true);
    setFailure(undefined);
    setOutcome(undefined);
    setGroup(undefined);
    try {
      const accepted = await requestMrpRun(facilityId);
      setWatching(accepted.mrp_run_id);
      void refresh(facilityId);
    } catch (error) {
      fail(error);
    } finally {
      setStarting(false);
    }
  };

  const startGroup = async () => {
    setGrouping(true);
    setFailure(undefined);
    setOutcome(undefined);
    setGroup(undefined);
    try {
      setGroup(await requestMrpGroupPass());
      void refresh(facilityId);
    } catch (error) {
      fail(error);
    } finally {
      setGrouping(false);
    }
  };

  const running = starting || watching !== undefined;
  const button = options.canRun ? (
    <Button
      variant="primary"
      busy={running}
      busyLabel={t("planning.mrp.running")}
      disabled={offline}
      data-testid="planning-mrp-run"
      onClick={() => void start()}
    >
      {t("planning.mrp.run")}
    </Button>
  ) : null;
  const groupButton =
    options.canRun && options.canGroup ? (
      <Button
        busy={grouping}
        busyLabel={t("planning.mrp.groupStarting")}
        disabled={offline}
        data-testid="planning-mrp-run-group"
        onClick={() => void startGroup()}
      >
        {t("planning.mrp.runGroup")}
      </Button>
    ) : null;

  let banner: ReactNode = null;
  if (failure !== undefined) {
    const code = failure instanceof RequestError ? failure.code : undefined;
    const text =
      code === "MRP_GROUP_SCOPE_REQUIRED"
        ? t("planning.mrp.groupScopeRequired")
        : code === "MRP_GROUP_PASS_RUNNING"
          ? groupRunningText(t, failure)
          : t("planning.mrp.requestFailed", { requestId: requestIdOf(failure) ?? "-" });
    banner = (
      <Banner kind="bad" data-testid="planning-mrp-request-failure" data-code={code}>
        {text}
      </Banner>
    );
  } else if (group !== undefined) {
    banner = (
      <Banner kind="ok" data-testid="planning-mrp-group-started">
        {t("planning.mrp.groupStarted", {
          order: group.facilities
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((f) => facilityName(f.facility_id))
            .join(" → "),
        })}
        {group.skipped.length > 0 ? (
          <span className="planning-mrp-skips" data-testid="planning-mrp-group-skipped">
            {" "}
            {t("planning.mrp.groupSkipped", {
              names: group.skipped.map((s) => facilityName(s.facility_id)).join(", "),
            })}
          </span>
        ) : null}
      </Banner>
    );
  } else if (outcome?.status === "succeeded") {
    banner = (
      <Banner kind="ok" data-testid="planning-mrp-run-done">
        {t("planning.mrp.runDone", {
          run: outcome.run_no,
          time: formatTimestamp(outcome.finished_at, i18n.language, timezone),
        })}
      </Banner>
    );
  } else if (outcome?.status === "failed") {
    banner = (
      <Banner kind="bad" data-testid="planning-mrp-run-failed">
        {t("planning.mrp.runFailed", { run: outcome.run_no })} <MrpErrorLine run={outcome} />
      </Banner>
    );
  } else if (offline && options.canRun) {
    banner = <p className="planning-note">{t("planning.common.offlineWrites")}</p>;
  }
  return { running, start: () => void start(), button, groupButton, banner };
}
